import React, { useState, useRef, useEffect, useCallback, KeyboardEvent } from 'react';
import { X, Github, CheckCircle2, AlertCircle, Loader2, ExternalLink, Settings, Shield, Globe, AlertTriangle, FilePlus, FileEdit, FileCheck, Trash2, ChevronDown, ChevronRight, MessageSquare, Send, Bot } from 'lucide-react';
import { GitHubStatus, publishNotebookToGitHub, previewGitHubDiff, NotebookDiffResult, ChapterDiff } from '../services/githubService';
import { sendDiffExplainMessage, DiffExplainSummary } from '../services/geminiService';
import { MindMapNode } from '../types';

interface Props {
    notebookId: string;
    notebookTitle: string;
    nodes: MindMapNode[];
    links?: { id: string; fromId: string; toId: string; createdAt: number; color?: string }[];
    lastPublishedAt?: number | null;
    status: GitHubStatus | null;
    onClose: () => void;
    onOpenSettings: () => void;
    language?: string;
    onPublishSuccess?: () => void;
}

type Stage = 'mode_select' | 'diff_preview' | 'publishing' | 'success' | 'error';
export type PublishMode = 'full' | 'safe';

// ─── Line diff (LCS-based) ────────────────────────────────────────────────────

type DiffLine = { t: 'add' | 'del' | 'ctx'; s: string };

function lcsLineDiff(a: string[], b: string[]): DiffLine[] {
    const m = a.length, n = b.length;
    const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
    for (let i = m - 1; i >= 0; i--)
        for (let j = n - 1; j >= 0; j--)
            dp[i][j] = a[i] === b[j] ? 1 + dp[i + 1][j + 1] : Math.max(dp[i + 1][j], dp[i][j + 1]);
    const out: DiffLine[] = [];
    let i = 0, j = 0;
    while (i < m || j < n) {
        if (i < m && j < n && a[i] === b[j]) {
            out.push({ t: 'ctx', s: a[i] }); i++; j++;
        } else if (j < n && (i >= m || (dp[i + 1]?.[j] ?? 0) <= (dp[i]?.[j + 1] ?? 0))) {
            out.push({ t: 'add', s: b[j] }); j++;
        } else {
            out.push({ t: 'del', s: a[i] }); i++;
        }
    }
    return out;
}

function diffSmallChunk(a: string[], b: string[]): DiffLine[] {
    if (a.join('\n') === b.join('\n')) return a.map(s => ({ t: 'ctx' as const, s }));
    const m = a.length, n = b.length;
    if (m * n <= 40000) return lcsLineDiff(a, b);
    return [...a.map(s => ({ t: 'del' as const, s })), ...b.map(s => ({ t: 'add' as const, s }))];
}

// Split lines into per-node chunks by <a id="node-..."> anchors, then diff chunk-by-chunk.
// Each chunk is small (one note), so LCS inside a chunk is always fast.
function anchorChunkedDiff(aMid: string[], bMid: string[]): DiffLine[] {
    type Chunk = { id: string | null; lines: string[] };
    const split = (lines: string[]): Chunk[] => {
        const chunks: Chunk[] = [];
        let cur: string[] = [], curId: string | null = null;
        for (const line of lines) {
            const hit = line.match(/<a id="(node-[^"]+)"><\/a>/);
            if (hit && cur.length > 0) { chunks.push({ id: curId, lines: cur }); curId = hit[1]; cur = [line]; }
            else { if (hit) curId = hit[1]; cur.push(line); }
        }
        if (cur.length > 0) chunks.push({ id: curId, lines: cur });
        return chunks;
    };

    const aChunks = split(aMid);
    const bChunks = split(bMid);
    const aById = new Map(aChunks.filter(c => c.id).map(c => [c.id!, c.lines]));
    const bIds = new Set(bChunks.filter(c => c.id).map(c => c.id!));
    const result: DiffLine[] = [];

    // Header chunk (progress line, before any anchor)
    const aHead = aChunks.find(c => !c.id);
    const bHead = bChunks.find(c => !c.id);
    if (aHead || bHead) result.push(...diffSmallChunk(aHead?.lines ?? [], bHead?.lines ?? []));

    // Anchored note chunks in new-file order
    for (const bc of bChunks) {
        if (!bc.id) continue;
        const al = aById.get(bc.id);
        if (!al) for (const s of bc.lines) result.push({ t: 'add', s });
        else result.push(...diffSmallChunk(al, bc.lines));
    }

    // Deleted chunks (in old but not in new)
    for (const ac of aChunks) {
        if (ac.id && !bIds.has(ac.id)) for (const s of ac.lines) result.push({ t: 'del', s });
    }

    return result;
}

function computeLineDiff(oldText: string, newText: string): DiffLine[] {
    const a = oldText.split('\n');
    const b = newText.split('\n');
    const m = a.length, n = b.length;

    // Trim common prefix/suffix to skip completely unchanged lines
    let pre = 0;
    while (pre < m && pre < n && a[pre] === b[pre]) pre++;
    let suf = 0;
    while (suf < m - pre && suf < n - pre && a[m - 1 - suf] === b[n - 1 - suf]) suf++;

    const aMid = a.slice(pre, m - suf);
    const bMid = b.slice(pre, n - suf);
    const mm = aMid.length, nn = bMid.length;

    let midDiff: DiffLine[] = [];
    if (mm > 0 || nn > 0) {
        if (mm * nn <= 2000000) {
            midDiff = lcsLineDiff(aMid, bMid);
        } else {
            // File too large for whole-file LCS — diff per note chunk
            midDiff = anchorChunkedDiff(aMid, bMid);
        }
    }

    return [
        ...a.slice(0, pre).map(s => ({ t: 'ctx' as const, s })),
        ...midDiff,
        ...a.slice(m - suf).map(s => ({ t: 'ctx' as const, s })),
    ];
}

// ─── Diff renderer (context-collapsed) ───────────────────────────────────────

const CONTEXT = 2;
const MAX_DIFF_LINES = 300;

function DiffViewer({ oldContent, newContent }: { oldContent: string; newContent: string }) {
    const lines = computeLineDiff(oldContent, newContent);

    const addCount = lines.filter(l => l.t === 'add').length;
    const delCount = lines.filter(l => l.t === 'del').length;

    // Collapse long unchanged stretches, keep CONTEXT lines around changes
    const isChanged = lines.map(l => l.t !== 'ctx');
    const visible = lines.map((_, idx) => {
        for (let d = -CONTEXT; d <= CONTEXT; d++) {
            if (isChanged[idx + d]) return true;
        }
        return false;
    });

    const rows: React.ReactNode[] = [];
    let hiddenCount = 0;
    let totalRendered = 0;

    for (let idx = 0; idx < lines.length; idx++) {
        if (totalRendered >= MAX_DIFF_LINES) {
            rows.push(
                <div key="truncate" className="text-[10px] text-gray-400 px-3 py-1 italic">
                    … truncated (file too large to display fully)
                </div>
            );
            break;
        }

        if (!visible[idx]) {
            hiddenCount++;
            if (idx === lines.length - 1 || visible[idx + 1]) {
                rows.push(
                    <div key={`hide-${idx}`} className="text-[10px] text-gray-400 dark:text-gray-500 px-3 py-0.5 bg-gray-50 dark:bg-slate-800/50 italic">
                        ··· {hiddenCount} unchanged line{hiddenCount !== 1 ? 's' : ''}
                    </div>
                );
                hiddenCount = 0;
            }
            continue;
        }

        const { t, s } = lines[idx];
        rows.push(
            <div
                key={idx}
                className={`text-[11px] font-mono px-3 py-px whitespace-pre overflow-hidden text-ellipsis ${
                    t === 'add' ? 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                    t === 'del' ? 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300' :
                    'text-gray-500 dark:text-gray-400'
                }`}
                title={s}
            >
                {t === 'add' ? '+' : t === 'del' ? '-' : ' '} {s}
            </div>
        );
        totalRendered++;
        if (!visible[idx] || hiddenCount > 0) hiddenCount = 0;
    }

    if (rows.length === 0) {
        return <div className="text-[11px] text-gray-400 px-3 py-2 italic">No textual differences found.</div>;
    }

    return (
        <div className="border-t dark:border-slate-700">
            <div className="flex gap-2 px-3 py-1 bg-gray-50 dark:bg-slate-800/60 text-[10px] font-medium">
                {addCount > 0 && <span className="text-green-600 dark:text-green-400">+{addCount} added</span>}
                {delCount > 0 && <span className="text-red-500">−{delCount} removed</span>}
            </div>
            <div className="overflow-y-auto max-h-80">{rows}</div>
        </div>
    );
}

// ─── Chapter row ──────────────────────────────────────────────────────────────

function ChapterRow({ cd, expanded, onToggle, vn }: {
    cd: ChapterDiff; expanded: boolean; onToggle: () => void; vn: boolean;
}) {
    const canExpand = cd.status === 'modified' && cd.oldContent != null && cd.newContent != null;
    const isNew = cd.status === 'new';

    const icon =
        cd.status === 'new' ? <FilePlus size={13} className="text-green-500 shrink-0" /> :
        cd.status === 'modified' ? <FileEdit size={13} className="text-amber-500 shrink-0" /> :
        <FileCheck size={13} className="text-gray-400 shrink-0" />;

    const label =
        cd.status === 'new' ? (vn ? 'Mới' : 'New') :
        cd.status === 'modified' ? (vn ? 'Đã sửa' : 'Modified') :
        (vn ? 'Không đổi' : 'Unchanged');

    const labelColor =
        cd.status === 'new' ? 'text-green-600 dark:text-green-400' :
        cd.status === 'modified' ? 'text-amber-600 dark:text-amber-400' :
        'text-gray-400 dark:text-gray-500';

    return (
        <div className={`rounded-lg overflow-hidden ${cd.status !== 'unchanged' ? 'border dark:border-slate-600' : ''}`}>
            <button
                onClick={canExpand || isNew ? onToggle : undefined}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                    canExpand || isNew
                        ? 'hover:bg-gray-100 dark:hover:bg-slate-700/60 cursor-pointer'
                        : 'cursor-default'
                } bg-gray-50 dark:bg-slate-700/40`}
            >
                {icon}
                <span className="flex-1 text-xs text-gray-700 dark:text-gray-300 truncate" title={cd.title}>
                    {cd.title}
                </span>
                <span className={`text-[11px] font-medium ${labelColor}`}>{label}</span>
                {(canExpand || isNew) && (
                    expanded
                        ? <ChevronDown size={12} className="text-gray-400 shrink-0" />
                        : <ChevronRight size={12} className="text-gray-400 shrink-0" />
                )}
            </button>

            {expanded && canExpand && (
                <DiffViewer oldContent={cd.oldContent!} newContent={cd.newContent!} />
            )}

            {expanded && isNew && cd.newContent != null && (
                <div className="overflow-y-auto max-h-48 border-t dark:border-slate-700">
                    {cd.newContent.split('\n').slice(0, 100).map((s, idx) => (
                        <div key={idx} className="text-[11px] font-mono px-3 py-px bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300 whitespace-pre overflow-hidden text-ellipsis">
                            + {s}
                        </div>
                    ))}
                    {cd.newContent.split('\n').length > 100 && (
                        <div className="text-[10px] text-gray-400 px-3 py-1 italic">… and more</div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Main modal ───────────────────────────────────────────────────────────────

export const GitHubPublishModal: React.FC<Props> = ({
    notebookId, notebookTitle, nodes, links, lastPublishedAt,
    status, onClose, onOpenSettings, language, onPublishSuccess,
}) => {
    const [stage, setStage] = useState<Stage>('mode_select');
    const [mode, setMode] = useState<PublishMode>('full');
    const [resultUrl, setResultUrl] = useState<string | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [diffResult, setDiffResult] = useState<NotebookDiffResult | null>(null);
    const [diffLoading, setDiffLoading] = useState(false);
    const [diffError, setDiffError] = useState<string | null>(null);
    const [expandedPath, setExpandedPath] = useState<string | null>(null);
    const hasStarted = useRef(false);

    // AI chat state
    type AiMsg = { role: 'user' | 'model'; text: string };
    const CHAT_STORAGE_KEY = `gh_diff_chat_${notebookId}`;
    const [aiChat, setAiChat] = useState<AiMsg[]>(() => {
        try { const s = localStorage.getItem(CHAT_STORAGE_KEY); return s ? JSON.parse(s) : []; } catch { return []; }
    });
    const [aiInput, setAiInput] = useState('');
    const [aiLoading, setAiLoading] = useState(false);
    const [showAiChat, setShowAiChat] = useState(() => {
        try { const s = localStorage.getItem(CHAT_STORAGE_KEY); return !!(s && JSON.parse(s).length > 0); } catch { return false; }
    });
    const aiScrollRef = useRef<HTMLDivElement>(null);

    const vn = language === 'vietnamese';
    const configured = !!status?.configured;

    useEffect(() => {
        if (stage !== 'diff_preview' || !configured) return;
        setDiffLoading(true);
        setDiffError(null);
        setDiffResult(null);
        setExpandedPath(null);
        previewGitHubDiff(notebookId, mode, links)
            .then(r => { setDiffResult(r); setDiffLoading(false); })
            .catch(e => { setDiffError(e.message || 'Failed to load diff'); setDiffLoading(false); });
    }, [stage, notebookId, mode, configured]);

    // Count notes marked for English translation
    const markedForTranslation = nodes.filter(n => !!(n as any).translateToEnglish);

    const run = async (selectedMode: PublishMode) => {
        if (!configured || hasStarted.current) return;
        hasStarted.current = true;
        setShowAiChat(false);
        setStage('publishing');
        setErrorMsg(null);
        try {
            const result = await publishNotebookToGitHub(notebookId, selectedMode);
            setResultUrl(result.url);
            setStage('success');
            onPublishSuccess?.();
        } catch (e: any) {
            setErrorMsg(e.message || 'Unknown error');
            setStage('error');
        }
    };

    const toggleExpand = useCallback((path: string) => {
        setExpandedPath(p => p === path ? null : path);
    }, []);

    const buildDiffContext = useCallback((): { summary: DiffExplainSummary; sampleLines: string[] } => {
        if (!diffResult) return { summary: { newFiles: 0, modifiedFiles: 0, deletedFiles: 0, unchangedFiles: 0 }, sampleLines: [] };
        const summary: DiffExplainSummary = {
            newFiles: diffResult.stats.newFiles,
            modifiedFiles: diffResult.stats.modifiedFiles,
            deletedFiles: diffResult.stats.deletedFiles,
            unchangedFiles: diffResult.stats.unchangedFiles,
            changedFileTitles: [
                ...diffResult.chapterDiffs.filter(c => c.status !== 'unchanged').map(c => c.title),
                ...diffResult.deletedPaths.map(p => p.split('/').pop()?.replace(/\.md$/, '') || p),
            ].slice(0, 20),
        };
        const sampleLines: string[] = [];
        for (const cd of diffResult.chapterDiffs) {
            if (cd.status === 'modified' && cd.oldContent && cd.newContent) {
                const diff = computeLineDiff(cd.oldContent, cd.newContent);
                const changed = diff.filter(l => l.t !== 'ctx');
                if (changed.length) {
                    sampleLines.push(`[${cd.title}]`);
                    changed.forEach(l => sampleLines.push(`${l.t === 'add' ? '+' : '-'} ${l.s}`));
                }
            } else if (cd.status === 'new' && cd.newContent) {
                sampleLines.push(`[${cd.title}] (new file)`);
                cd.newContent.split('\n').slice(0, 60).forEach(l => sampleLines.push(`+ ${l}`));
            }
            if (sampleLines.length >= 3000) break;
        }
        return { summary, sampleLines };
    }, [diffResult]);

    const saveAiChat = useCallback((chat: AiMsg[]) => {
        try { localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(chat.slice(-40))); } catch {}
    }, [CHAT_STORAGE_KEY]);

    const sendAiMessage = useCallback(async (text: string) => {
        if (!text.trim() || aiLoading || !diffResult) return;
        const msg = text.trim();
        setAiInput('');
        setShowAiChat(true);
        const newHistory: AiMsg[] = [...aiChat, { role: 'user', text: msg }];
        setAiChat(newHistory);
        saveAiChat(newHistory);
        setAiLoading(true);
        setTimeout(() => aiScrollRef.current?.scrollTo({ top: aiScrollRef.current.scrollHeight, behavior: 'smooth' }), 50);
        try {
            const { summary, sampleLines } = buildDiffContext();
            const reply = await sendDiffExplainMessage(
                aiChat.map(m => ({ role: m.role, text: m.text })),
                msg,
                notebookTitle,
                summary,
                sampleLines,
                language === 'vietnamese' ? 'vietnamese' : 'english'
            );
            setAiChat(prev => { const next = [...prev, { role: 'model' as const, text: reply }]; saveAiChat(next); return next; });
        } catch (e: any) {
            setAiChat(prev => { const next = [...prev, { role: 'model' as const, text: vn ? `Lỗi: ${e.message}` : `Error: ${e.message}` }]; saveAiChat(next); return next; });
        } finally {
            setAiLoading(false);
            setTimeout(() => aiScrollRef.current?.scrollTo({ top: aiScrollRef.current.scrollHeight, behavior: 'smooth' }), 100);
        }
    }, [aiChat, aiLoading, diffResult, buildDiffContext, notebookTitle, language, vn]);

    // ── shared header ──────────────────────────────────────────────────────────
    const header = (
        <div className="flex items-center gap-3 px-5 py-4 border-b dark:border-slate-700 bg-gray-50 dark:bg-slate-900 shrink-0">
            <Github size={18} className="text-gray-700 dark:text-gray-300 shrink-0" />
            <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-gray-900 dark:text-white truncate">
                    {vn ? 'Publish lên GitHub' : 'Publish to GitHub'}
                </p>
                <p className="text-xs text-gray-500 truncate">{notebookTitle}</p>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-400 transition-colors">
                <X size={16} />
            </button>
        </div>
    );

    // ── diff list (shared between single + dual column) ─────────────────────────
    const diffFileList = diffResult ? (
        <>
            <div className="space-y-1.5 overflow-y-auto pr-0.5 flex-1 min-h-0 max-h-[55vh]">
                {diffResult.overviewStatus !== 'absent' && (() => {
                    const cd: ChapterDiff = { path: '_overview', title: vn ? 'Tổng quan' : 'Overview', status: diffResult.overviewStatus as any };
                    return <ChapterRow key="_overview" cd={cd} expanded={expandedPath === '_overview'} onToggle={() => toggleExpand('_overview')} vn={vn} />;
                })()}
                {diffResult.chapterDiffs.map(cd => (
                    <ChapterRow key={cd.path} cd={cd} expanded={expandedPath === cd.path} onToggle={() => toggleExpand(cd.path)} vn={vn} />
                ))}
                {diffResult.deletedPaths.map(p => {
                    const name = p.split('/').pop()?.replace(/\.md$/, '').replace(/_/g, ' ') || p;
                    return (
                        <div key={p} className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                            <Trash2 size={13} className="text-red-400 shrink-0" />
                            <span className="flex-1 text-xs text-gray-700 dark:text-gray-300 truncate">{name}</span>
                            <span className="text-[11px] font-medium text-red-500">{vn ? 'Xóa' : 'Deleted'}</span>
                        </div>
                    );
                })}
            </div>
            <div className="flex items-center gap-3 text-[11px] border-t dark:border-slate-700 pt-2 shrink-0">
                {diffResult.stats.newFiles > 0 && <span className="text-green-600 dark:text-green-400 font-medium">+{diffResult.stats.newFiles} {vn ? 'mới' : 'new'}</span>}
                {diffResult.stats.modifiedFiles > 0 && <span className="text-amber-600 dark:text-amber-400 font-medium">~{diffResult.stats.modifiedFiles} {vn ? 'sửa' : 'changed'}</span>}
                {diffResult.stats.deletedFiles > 0 && <span className="text-red-500 font-medium">-{diffResult.stats.deletedFiles} {vn ? 'xóa' : 'removed'}</span>}
                {diffResult.stats.unchangedFiles > 0 && <span className="text-gray-400">{diffResult.stats.unchangedFiles} {vn ? 'không đổi' : 'unchanged'}</span>}
                {diffResult.stats.newFiles === 0 && diffResult.stats.modifiedFiles === 0 && diffResult.stats.deletedFiles === 0 && (
                    <span className="text-gray-400">{vn ? 'Không có thay đổi nào.' : 'No changes detected.'}</span>
                )}
                {(diffResult.crossLinkCount ?? 0) > 0 && (
                    <span className="text-purple-500 dark:text-purple-400 ml-auto">🔗 {diffResult.crossLinkCount} cross-link{diffResult.crossLinkCount !== 1 ? 's' : ''}</span>
                )}
            </div>
        </>
    ) : null;

    // ── AI chat panel (right column) ────────────────────────────────────────────
    const aiPanel = (
        <div className="flex-1 flex flex-col bg-gray-50 dark:bg-slate-900/60 min-w-0 min-h-0">
            <div className="flex items-center gap-2 px-4 py-3 border-b dark:border-slate-700 bg-indigo-50 dark:bg-indigo-950/50 shrink-0">
                <Bot size={15} className="text-indigo-500 shrink-0" />
                <span className="flex-1 text-sm font-semibold text-indigo-700 dark:text-indigo-300">
                    {vn ? 'AI Giải thích thay đổi' : 'AI Diff Assistant'}
                </span>
                <button onClick={() => setShowAiChat(false)} className="p-1 rounded-md hover:bg-indigo-100 dark:hover:bg-indigo-900/40 text-indigo-400 transition-colors">
                    <X size={13} />
                </button>
            </div>

            <div ref={aiScrollRef} className="flex-1 overflow-y-auto p-4 flex flex-col gap-3 min-h-0">
                {aiChat.length === 0 && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-2">
                        {vn ? 'Chọn câu hỏi hoặc tự nhập bên dưới' : 'Choose a question or type below'}
                    </p>
                )}
                {aiChat.map((m, i) => (
                    <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        {m.role === 'model' && <Bot size={14} className="text-indigo-400 shrink-0 mt-1" />}
                        <div className={`text-xs leading-relaxed rounded-xl px-3 py-2 max-w-[85%] whitespace-pre-wrap ${
                            m.role === 'user'
                                ? 'bg-indigo-500 text-white rounded-br-sm'
                                : 'bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 border dark:border-slate-700 rounded-bl-sm'
                        }`}>{m.text}</div>
                    </div>
                ))}
                {aiLoading && (
                    <div className="flex gap-2 items-center">
                        <Bot size={14} className="text-indigo-400 shrink-0" />
                        <div className="flex gap-1 px-3 py-2.5 bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-xl rounded-bl-sm">
                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                    </div>
                )}
            </div>

            {/* Quick question chips — always visible */}
            <div className="px-3 pt-2 pb-1 border-t dark:border-slate-700 bg-white dark:bg-slate-800 shrink-0">
                <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-none">
                    {[
                        vn ? 'Tao mất nội dung không?' : 'Did I lose content?',
                        vn ? 'Tóm tắt thay đổi' : 'Summarize changes',
                        vn ? 'File nào thay đổi nhiều nhất?' : 'Most changed file?',
                        vn ? 'Tại sao có nhiều dòng vậy?' : 'Why so many lines?',
                    ].map(q => (
                        <button key={q} onClick={() => sendAiMessage(q)} disabled={aiLoading}
                            className="shrink-0 text-[10px] px-2 py-1 rounded-full border border-indigo-200 dark:border-indigo-700/60 text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors disabled:opacity-40 whitespace-nowrap">
                            {q}
                        </button>
                    ))}
                    {aiChat.length > 0 && (
                        <button onClick={() => { const empty: AiMsg[] = []; setAiChat(empty); saveAiChat(empty); }}
                            className="shrink-0 text-[10px] px-2 py-1 rounded-full border border-gray-200 dark:border-slate-600 text-gray-400 hover:text-red-500 hover:border-red-300 transition-colors whitespace-nowrap">
                            {vn ? 'Xóa lịch sử' : 'Clear history'}
                        </button>
                    )}
                </div>
            </div>
            <div className="flex gap-2 px-4 py-2.5 bg-white dark:bg-slate-800 shrink-0">
                <input type="text" value={aiInput} onChange={e => setAiInput(e.target.value)}
                    onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendAiMessage(aiInput); } }}
                    placeholder={vn ? 'Hỏi về thay đổi...' : 'Ask about the changes...'}
                    disabled={aiLoading}
                    className="flex-1 text-sm px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-xl bg-gray-50 dark:bg-slate-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:opacity-50"
                />
                <button onClick={() => sendAiMessage(aiInput)} disabled={aiLoading || !aiInput.trim()}
                    className="px-3 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    <Send size={14} />
                </button>
            </div>
        </div>
    );

    return (
        <div className="fixed inset-0 z-[10100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            {/* ── DUAL COLUMN (diff_preview + AI open) ──────────────────── */}
            {configured && stage === 'diff_preview' && showAiChat ? (
                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border dark:border-slate-700 w-full max-w-5xl overflow-hidden flex flex-col" style={{ height: '82vh' }}>
                    {header}
                    <div className="flex flex-row flex-1 min-h-0 divide-x dark:divide-slate-700">
                        {/* Left: diff list */}
                        <div className="w-[42%] shrink-0 flex flex-col gap-3 p-5 min-h-0 overflow-hidden">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 shrink-0">{vn ? 'So sánh với GitHub hiện tại:' : 'Comparing with current GitHub:'}</p>
                            {diffLoading ? (
                                <div className="flex flex-col items-center gap-3 py-8 text-gray-500">
                                    <Loader2 size={24} className="animate-spin text-blue-500" />
                                    <p className="text-xs">{vn ? 'Đang so sánh…' : 'Comparing…'}</p>
                                </div>
                            ) : diffError ? (
                                <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
                                    <AlertCircle size={14} className="text-red-500 shrink-0 mt-0.5" />
                                    <p className="text-xs text-red-600 dark:text-red-400">{diffError}</p>
                                </div>
                            ) : diffResult ? (
                                <>
                                    <div className="space-y-1.5 overflow-y-auto flex-1 min-h-0 pr-0.5">
                                        {diffResult.overviewStatus !== 'absent' && (() => {
                                            const cd: ChapterDiff = { path: '_overview', title: vn ? 'Tổng quan' : 'Overview', status: diffResult.overviewStatus as any };
                                            return <ChapterRow key="_overview" cd={cd} expanded={expandedPath === '_overview'} onToggle={() => toggleExpand('_overview')} vn={vn} />;
                                        })()}
                                        {diffResult.chapterDiffs.map(cd => (
                                            <ChapterRow key={cd.path} cd={cd} expanded={expandedPath === cd.path} onToggle={() => toggleExpand(cd.path)} vn={vn} />
                                        ))}
                                        {diffResult.deletedPaths.map(p => {
                                            const name = p.split('/').pop()?.replace(/\.md$/, '').replace(/_/g, ' ') || p;
                                            return (
                                                <div key={p} className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                                                    <Trash2 size={13} className="text-red-400 shrink-0" />
                                                    <span className="flex-1 text-xs text-gray-700 dark:text-gray-300 truncate">{name}</span>
                                                    <span className="text-[11px] font-medium text-red-500">{vn ? 'Xóa' : 'Deleted'}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="flex items-center gap-3 text-[11px] border-t dark:border-slate-700 pt-2 shrink-0">
                                        {diffResult.stats.newFiles > 0 && <span className="text-green-600 dark:text-green-400 font-medium">+{diffResult.stats.newFiles} {vn ? 'mới' : 'new'}</span>}
                                        {diffResult.stats.modifiedFiles > 0 && <span className="text-amber-600 dark:text-amber-400 font-medium">~{diffResult.stats.modifiedFiles} {vn ? 'sửa' : 'changed'}</span>}
                                        {diffResult.stats.deletedFiles > 0 && <span className="text-red-500 font-medium">-{diffResult.stats.deletedFiles} {vn ? 'xóa' : 'removed'}</span>}
                                        {diffResult.stats.unchangedFiles > 0 && <span className="text-gray-400">{diffResult.stats.unchangedFiles} {vn ? 'không đổi' : 'unchanged'}</span>}
                                    </div>
                                </>
                            ) : null}
                            <div className="flex gap-2 shrink-0 mt-auto pt-1">
                                <button onClick={() => setStage('mode_select')} className="flex-1 py-2 border border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-400 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                                    {vn ? 'Quay lại' : 'Back'}
                                </button>
                                <button onClick={() => run(mode)} disabled={diffLoading} className="flex-1 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                                    <Github size={14} />
                                    {vn ? 'Publish' : 'Publish'}
                                </button>
                            </div>
                        </div>
                        {/* Right: AI chat */}
                        {aiPanel}
                    </div>
                </div>

            ) : (
            /* ── SINGLE COLUMN (all other stages / chat closed) ──────── */
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border dark:border-slate-700 w-full max-w-lg overflow-hidden">
                {header}
                <div className="p-6">
                    {!configured ? (
                        <div className="text-center py-4">
                            <Github size={36} className="text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
                                {vn ? 'Chưa cấu hình GitHub' : 'GitHub not configured'}
                            </p>
                            <p className="text-xs text-gray-500 mb-4">
                                {vn ? 'Mở GitHub Hub để kết nối và chọn repo.' : 'Open GitHub Hub to connect and choose a repo.'}
                            </p>
                            <button onClick={onOpenSettings} className="flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors mx-auto">
                                <Settings size={14} /> {vn ? 'Mở GitHub Hub' : 'Open GitHub Hub'}
                            </button>
                        </div>

                    ) : stage === 'mode_select' ? (
                        <div className="space-y-4">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {vn ? 'Chọn chế độ publish:' : 'Choose publish mode:'}
                            </p>
                            <button onClick={() => setMode('full')} className={`w-full flex items-start gap-3 p-3.5 rounded-xl border-2 text-left transition-colors ${mode === 'full' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-slate-600 hover:border-gray-300'}`}>
                                <Shield size={18} className="text-blue-500 mt-0.5 shrink-0" />
                                <div>
                                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{vn ? 'Đầy đủ' : 'Full Archive'}</p>
                                    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{vn ? 'Tất cả notes, ảnh nguồn, AI feedback. Phù hợp cho repo private.' : 'All notes, source screenshots, AI feedback. Best for private repos.'}</p>
                                </div>
                            </button>
                            <button onClick={() => setMode('safe')} className={`w-full flex items-start gap-3 p-3.5 rounded-xl border-2 text-left transition-colors ${mode === 'safe' ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : 'border-gray-200 dark:border-slate-600 hover:border-gray-300'}`}>
                                <Globe size={18} className="text-green-500 mt-0.5 shrink-0" />
                                <div>
                                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{vn ? 'An toàn' : 'Safe Mode'}</p>
                                    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{vn ? 'Chỉ text giải thích của bạn, không có ảnh từ sách/PDF. Phù hợp cho repo public.' : 'Your explanations only — no source screenshots from books/PDFs. Good for public repos.'}</p>
                                </div>
                            </button>
                            <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                                <AlertTriangle size={13} className="text-amber-500 shrink-0 mt-0.5" />
                                <p className="text-[11px] text-amber-700 dark:text-amber-400 leading-relaxed">
                                    {vn ? 'Nếu publish lên repo public, hãy đảm bảo bạn có quyền chia sẻ ảnh từ sách/PDF bản quyền. Dùng chế độ An toàn nếu không chắc chắn.' : 'If publishing to a public repo, make sure you have the right to share screenshots from copyrighted books/PDFs. Use Safe Mode when unsure.'}
                                </p>
                            </div>
                            {/* English translation info */}
                            {markedForTranslation.length > 0 && (
                                <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                                    <Globe size={13} className="text-blue-500 shrink-0 mt-0.5" />
                                    <p className="text-[11px] text-blue-700 dark:text-blue-400 leading-relaxed">
                                        {vn
                                            ? `${markedForTranslation.length} note sẽ được dịch sang tiếng Anh khi publish (chỉ dịch, không thay đổi nội dung).`
                                            : `${markedForTranslation.length} note${markedForTranslation.length > 1 ? 's' : ''} will be translated to English on publish (translation only, content unchanged).`}
                                    </p>
                                </div>
                            )}

                            <div className="flex gap-2 pt-1">
                                <button onClick={onClose} className="flex-1 py-2 border border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-400 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                                    {vn ? 'Hủy' : 'Cancel'}
                                </button>
                                <button onClick={() => setStage('diff_preview')} className="flex-1 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors flex items-center justify-center gap-2">
                                    <Github size={14} />
                                    {vn ? 'Xem thay đổi' : 'Preview changes'}
                                </button>
                            </div>
                        </div>

                    ) : stage === 'diff_preview' ? (
                        <div className="space-y-3">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {vn ? 'So sánh với GitHub hiện tại:' : 'Comparing with current GitHub:'}
                            </p>
                            {diffLoading ? (
                                <div className="flex flex-col items-center gap-3 py-8 text-gray-500">
                                    <Loader2 size={24} className="animate-spin text-blue-500" />
                                    <p className="text-xs">{vn ? 'Đang so sánh nội dung với GitHub…' : 'Comparing content with GitHub…'}</p>
                                </div>
                            ) : diffError ? (
                                <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
                                    <AlertCircle size={14} className="text-red-500 shrink-0 mt-0.5" />
                                    <p className="text-xs text-red-600 dark:text-red-400">{diffError}</p>
                                </div>
                            ) : diffResult ? (
                                <>
                                    <div className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-0.5">
                                        {diffResult.overviewStatus !== 'absent' && (() => {
                                            const cd: ChapterDiff = { path: '_overview', title: vn ? 'Tổng quan' : 'Overview', status: diffResult.overviewStatus as any };
                                            return <ChapterRow key="_overview" cd={cd} expanded={expandedPath === '_overview'} onToggle={() => toggleExpand('_overview')} vn={vn} />;
                                        })()}
                                        {diffResult.chapterDiffs.map(cd => (
                                            <ChapterRow key={cd.path} cd={cd} expanded={expandedPath === cd.path} onToggle={() => toggleExpand(cd.path)} vn={vn} />
                                        ))}
                                        {diffResult.deletedPaths.map(p => {
                                            const name = p.split('/').pop()?.replace(/\.md$/, '').replace(/_/g, ' ') || p;
                                            return (
                                                <div key={p} className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                                                    <Trash2 size={13} className="text-red-400 shrink-0" />
                                                    <span className="flex-1 text-xs text-gray-700 dark:text-gray-300 truncate">{name}</span>
                                                    <span className="text-[11px] font-medium text-red-500">{vn ? 'Xóa' : 'Deleted'}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="flex items-center gap-3 text-[11px] border-t dark:border-slate-700 pt-2">
                                        {diffResult.stats.newFiles > 0 && <span className="text-green-600 dark:text-green-400 font-medium">+{diffResult.stats.newFiles} {vn ? 'mới' : 'new'}</span>}
                                        {diffResult.stats.modifiedFiles > 0 && <span className="text-amber-600 dark:text-amber-400 font-medium">~{diffResult.stats.modifiedFiles} {vn ? 'sửa' : 'changed'}</span>}
                                        {diffResult.stats.deletedFiles > 0 && <span className="text-red-500 font-medium">-{diffResult.stats.deletedFiles} {vn ? 'xóa' : 'removed'}</span>}
                                        {diffResult.stats.unchangedFiles > 0 && <span className="text-gray-400">{diffResult.stats.unchangedFiles} {vn ? 'không đổi' : 'unchanged'}</span>}
                                        {diffResult.stats.newFiles === 0 && diffResult.stats.modifiedFiles === 0 && diffResult.stats.deletedFiles === 0 && (
                                            <span className="text-gray-400">{vn ? 'Không có thay đổi nào.' : 'No changes detected.'}</span>
                                        )}
                                    </div>
                                </>
                            ) : null}
                            <div className="flex gap-2 pt-1">
                                <button onClick={() => setStage('mode_select')} className="flex-1 py-2 border border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-400 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                                    {vn ? 'Quay lại' : 'Back'}
                                </button>
                                <button onClick={() => run(mode)} disabled={diffLoading} className="flex-1 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                                    <Github size={14} />
                                    {vn ? 'Xác nhận Publish' : 'Confirm Publish'}
                                </button>
                            </div>
                            {diffResult && (
                                <button onClick={() => setShowAiChat(true)}
                                    className="w-full flex items-center justify-center gap-2 py-2 rounded-xl border border-indigo-200 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-300 text-xs font-medium hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors">
                                    <Bot size={13} />
                                    {vn ? 'Hỏi AI về thay đổi này' : 'Ask AI about these changes'}
                                </button>
                            )}
                        </div>

                    ) : stage === 'publishing' ? (
                        <div className="space-y-4 text-center py-2">
                            <Loader2 size={32} className="animate-spin text-blue-500 mx-auto" />
                            <div>
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{vn ? 'Đang publish…' : 'Publishing…'}</p>
                                <p className="text-xs text-gray-500 mt-0.5">{status?.repo} / {status?.branch}</p>
                            </div>
                            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 border border-blue-100 dark:border-blue-800">
                                <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
                                    {vn ? 'Đang chạy trên server. Bạn có thể đóng cửa sổ này và làm việc khác — icon GitHub ở sidebar sẽ quay khi đang chạy.' : "Running on the server. You can close this and do something else — the GitHub icon in the sidebar will spin while it's running."}
                                </p>
                            </div>
                            <button onClick={onClose} className="w-full py-2.5 border border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                                {vn ? 'Đóng, tiếp tục làm việc' : 'Close & keep working'}
                            </button>
                        </div>

                    ) : stage === 'success' ? (
                        <div className="text-center py-2 space-y-4">
                            <CheckCircle2 size={40} className="text-green-500 mx-auto" />
                            <div>
                                <p className="font-semibold text-gray-900 dark:text-white mb-1">{vn ? 'Đã publish thành công!' : 'Published successfully!'}</p>
                                <p className="text-xs text-gray-500">{vn ? 'Notebook đã được đẩy lên GitHub.' : 'Your notebook has been pushed to GitHub.'}</p>
                            </div>
                            {resultUrl && (
                                <a href={resultUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors">
                                    <Github size={14} />
                                    {vn ? 'Xem trên GitHub' : 'View on GitHub'}
                                    <ExternalLink size={12} />
                                </a>
                            )}
                        </div>

                    ) : (
                        <div className="space-y-4">
                            <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
                                <AlertCircle size={16} className="text-red-500 shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-red-700 dark:text-red-400">{vn ? 'Publish thất bại' : 'Publish failed'}</p>
                                    <p className="text-xs text-red-600 dark:text-red-400 mt-0.5 break-words">{errorMsg}</p>
                                    {errorMsg?.toLowerCase().includes('github') && (
                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                            {vn ? '(Lỗi này đến từ GitHub, không phải StudyBoard)' : '(This error is from GitHub, not StudyBoard)'}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => { hasStarted.current = false; setStage('mode_select'); }} className="flex-1 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors">
                                    {vn ? 'Thử lại' : 'Retry'}
                                </button>
                                <button onClick={onOpenSettings} className="flex-1 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                                    Settings
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            )}
        </div>
    );
};
